import { ScopeContext, useManager, useScope } from 'dreamstate';
import { FocusEvent, LegacyRef, ReactElement, useCallback, useState } from 'react';

import { IconButton, Menu, MenuButton, MenuItem, MenuList, rem } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Logger } from '@vimeo/logging';
import { useLiveStreamBroadcasterTracking } from '@vimeo/picox';

import { useControlledPopover } from '../../../hooks/dom/useControlledPopover';
import { IQnAContext, QnAManager } from '../../../store/firebase/interaction/qna';
import {
  trackClickEndQna,
  trackClickStartQna,
  trackExportQna,
} from '../../../tracking/tracking_engagement_qna';
import {
  T_DOWNLOAD,
  T_DOWNLOAD_QNA,
  T_SETTINGS,
  T_START_QNA,
  T_STOP_QNA,
} from '../../../translations';
import { createDomName } from '../../../utils/dom/identifying';
import { BokehTooltip } from '../../bokeh';
import { LiveErrorBoundary } from '../../error/boundary';
import { DownloadModal } from '../shared/DownloadModal';
import { InteractionToolsSubmenu } from '../shared/InteractionToolsSubmenu';

interface IQnaSettingsButtonProps extends IBaseDomElementProps {
  withInteractionSubmenu?: boolean;
  buttonIcon: ReactElement;
  qnaContext?: IQnAContext;
}

export function QnaSettingsButton({
  id = createDomName('qna-settings-button'),
  className = createDomName('qna-settings-button'),
  buttonIcon,
  withInteractionSubmenu = false,
  qnaContext: {
    isEventModerated,
    activeSessionId,
    qnaSessionsHistory,
    qnaActions: { createQnASession, openQnASession, closeQnASession },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(QnAManager),
}: IQnaSettingsButtonProps) {
  const {
    trackLiveStreamQaEnded,
    trackLiveStreamQaOverflowActionClicked,
    trackLiveStreamQaStarted,
  } = useLiveStreamBroadcasterTracking();
  const scopeContext: ScopeContext = useScope();
  const [isSessionLoading, setIsSessionLoading] = useState<boolean>(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState<boolean>(false);

  const { triggerRef, contentRef, isOpen, onOpenPopover, onClosePopover } = useControlledPopover();

  const onOpenMenu = useCallback(() => {
    onOpenPopover();
  }, [onOpenPopover]);

  const onClickOpenDownloadModal = useCallback(() => {
    setIsDownloadOpen(true);
    onClosePopover();
  }, [onClosePopover]);

  const onClickCloseDownloadModal = useCallback(() => setIsDownloadOpen(false), []);

  const onClickDownload = useCallback(() => {
    trackLiveStreamQaOverflowActionClicked({ liveStreamQaAction: 'download' });
    trackExportQna();
    setIsDownloadOpen(false);
  }, [trackLiveStreamQaOverflowActionClicked]);

  const onEndSession = useCallback(async () => {
    const { activeSessionQuestions, activeSessionPendingQuestions } =
      scopeContext.getContextOf(QnAManager);

    await closeQnASession();

    trackLiveStreamQaEnded();

    trackClickEndQna(
      isEventModerated,
      Object.keys(activeSessionQuestions).length +
        Object.keys(activeSessionPendingQuestions).length,
    );
  }, [scopeContext, closeQnASession, isEventModerated, trackLiveStreamQaEnded]);

  const onStartSession = useCallback(async () => {
    if (isEventModerated !== null) {
      try {
        setIsSessionLoading(true);
        const sessionId = await createQnASession(isEventModerated);

        await openQnASession(sessionId);
        trackLiveStreamQaStarted();
      } catch (error) {
        setIsSessionLoading(false);
        Logger.getGlobal().error('Failed to start qna session:', error);
      } finally {
        setIsSessionLoading(false);
        trackClickStartQna(isEventModerated);
      }
    }
  }, [isEventModerated, createQnASession, openQnASession, trackLiveStreamQaStarted]);

  const onChangeQnaSessionState = useCallback(() => {
    if (activeSessionId) {
      onEndSession();
    } else {
      onStartSession();
    }
  }, [activeSessionId, onEndSession, onStartSession]);

  const onFocus = useCallback((event: FocusEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  return (
    <LiveErrorBoundary component={'QnaSettingsButton'} isDetailed={false}>
      <Menu
        isOpen={isOpen}
        placement={'bottom'}
        closeOnSelect={false}
        onOpen={onOpenMenu}
        onClose={onClosePopover}
      >
        <BokehTooltip label={isOpen ? null : T_SETTINGS} placement={'bottom'}>
          <MenuButton
            as={IconButton}
            ref={triggerRef}
            id={id}
            className={className}
            aria-label={'settings'}
            icon={buttonIcon}
            size={'sm'}
            variant={'tertiary'}
            onFocus={onFocus}
          />
        </BokehTooltip>

        <MenuList
          ref={contentRef as LegacyRef<HTMLDivElement>}
          overflow={'unset'}
          width={rem(240)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: rem(4),
          }}
        >
          <MenuItem
            isDisabled={!activeSessionId && isSessionLoading}
            onClick={onChangeQnaSessionState}
          >
            {activeSessionId ? T_STOP_QNA : T_START_QNA}
          </MenuItem>

          <MenuItem onClick={onClickOpenDownloadModal}>{T_DOWNLOAD}</MenuItem>

          {withInteractionSubmenu ? (
            <>
              <InteractionToolsSubmenu
                interactionToolsContext={'qna'}
                placement={'right-start'}
                onCloseParentMenu={onClosePopover}
              />
            </>
          ) : null}
        </MenuList>
      </Menu>

      <DownloadModal
        header={T_DOWNLOAD_QNA}
        prefix={'export_qna'}
        history={qnaSessionsHistory}
        isOpen={isDownloadOpen}
        onClose={onClickCloseDownloadModal}
        onDownload={onClickDownload}
      />
    </LiveErrorBoundary>
  );
}
